import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserCog, Plus, Pencil, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { validateEmail } from "@/lib/validation";

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "sales_rep", phone: "", employee_id: "", status: "active" });
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get("/users");
      setUsers(res.data.users || []);
    } catch { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleSave = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    if (!editUser && !form.password) { toast.error("Password is required for new users"); return; }
    setSaving(true);
    try {
      if (editUser) {
        const updateData = { ...form };
        if (!updateData.password) delete updateData.password;
        await api.put(`/users/${editUser.user_id}`, updateData);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setDialogOpen(false); fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role, phone: u.phone || "", employee_id: u.employee_id || "", status: u.status });
    setDialogOpen(true);
  };

  const openAdd = () => {
    setEditUser(null);
    setForm({ name: "", email: "", password: "", role: "sales_rep", phone: "", employee_id: "", status: "active" });
    setDialogOpen(true);
  };

  const roleBadge = (role) => {
    if (role === "admin") return "bg-red-50 text-red-700 border-red-200";
    if (role === "sales_manager") return "bg-blue-50 text-blue-700 border-blue-200";
    return "bg-gray-50 text-gray-700 border-gray-200";
  };

  if (user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <p>Access denied. Admin only.</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#1F2937] tracking-tight">User Management</h1>
          <p className="text-sm text-gray-500 mt-1">Manage user accounts and roles</p>
        </div>
        <Button data-testid="add-user-button" onClick={openAdd} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
          <Plus className="w-4 h-4 mr-1.5" /> Add User
        </Button>
      </div>

      <Card className="border border-gray-200 rounded-sm shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Employee ID</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(3)].map((_, i) => <tr key={i} className="border-b border-gray-100"><td colSpan={6} className="px-4 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td></tr>)
              ) : users.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-400">
                  <UserCog className="w-10 h-10 mx-auto mb-3 text-gray-300" /><p>No users found</p>
                </td></tr>
              ) : (
                users.map((u) => (
                  <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-[#1F2937]">{u.name}</td>
                    <td className="px-4 py-3 text-gray-600">{u.email}</td>
                    <td className="px-4 py-3"><Badge className={`rounded-full text-xs capitalize ${roleBadge(u.role)}`}>{u.role?.replace("_", " ")}</Badge></td>
                    <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{u.employee_id || "-"}</td>
                    <td className="px-4 py-3">
                      <Badge className={`rounded-full text-xs ${u.status === "active" ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-50 text-gray-600 border-gray-200"}`}>
                        {u.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button data-testid={`edit-user-${u.user_id}`} variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-[#E63946]" onClick={() => openEdit(u)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading font-semibold text-xl">{editUser ? "Edit User" : "Add New User"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Name <span className="text-[#E63946]">*</span></label>
              <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm border-gray-300 h-10" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Email <span className="text-[#E63946]">*</span></label>
              <UserEmailField form={form} setForm={setForm} />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Password {!editUser && <span className="text-[#E63946]">*</span>}</label>
              <Input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editUser ? "Leave blank to keep" : ""} className="rounded-sm border-gray-300 h-10" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Role</label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="rounded-sm border-gray-300 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="sales_manager">Sales Manager</SelectItem>
                  <SelectItem value="sales_rep">Sales Rep</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Phone</label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm border-gray-300 h-10" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Employee ID</label>
              <Input value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="rounded-sm border-gray-300 h-10" />
            </div>
            {editUser && (
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="user-status-select" className="rounded-sm border-gray-300 h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-user-button" onClick={handleSave} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
              {saving ? "Saving..." : "Save User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function UserEmailField({ form, setForm }) {
  const [emailErr, setEmailErr] = useState(null);
  const [emailOk, setEmailOk] = useState(false);
  const checkEmail = () => {
    if (!form.email) { setEmailErr(null); setEmailOk(false); return; }
    const r = validateEmail(form.email);
    setEmailErr(r.valid ? null : r.error);
    setEmailOk(r.valid);
  };
  return (
    <>
      <div className="relative">
        <Input data-testid="user-email-input" type="email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} onBlur={checkEmail}
          className={`rounded-sm h-10 pr-8 ${emailErr ? "border-[#E63946]" : "border-gray-300"}`} />
        {emailOk && <CheckCircle2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#10B981]" />}
      </div>
      {emailErr && <p className="text-xs text-[#E63946] mt-1">{emailErr}</p>}
    </>
  );
}
